/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ListTree } from 'lucide-react';
import {
  getOptionsAtDepth,
  hasSelectableHierarchy,
  setHierarchyLevel,
} from '../../utils/serviceRequestHierarchy';

/**
 * Card of cascading dropdowns for picking an option path from a service
 * request type's hierarchy. Every level is shown at once and is optional; a
 * level's options appear only once the level above is picked, and changing a
 * level resets the levels below it. Renders nothing while loading or when the
 * type has no hierarchy configured. `nodes` are normalized nodes (see
 * utils/serviceRequestHierarchy.js). With `inline`, renders only the level
 * fields (no card/grid) so they flow into the parent form's grid.
 */
const HierarchySelector = ({
  levels = [],
  nodes = [],
  value = {},
  onChange,
  loading = false,
  selectClassName,
  inline = false,
}) => {
  if (loading || !hasSelectableHierarchy(levels, nodes)) return null;

  const fields = levels.map((level, depth) => {
    const parentUid = depth === 0 ? null : value[depth - 1];
    const parentPicked = depth === 0 || !!parentUid;
    const options = parentPicked ? getOptionsAtDepth(nodes, depth, parentUid) : [];
    const fieldId = `hierarchy-${level.key || depth}`;
    let placeholder = `— Select ${level.name.toLowerCase()} —`;
    if (!parentPicked) {
      placeholder = `— Select ${levels[depth - 1].name.toLowerCase()} first —`;
    } else if (options.length === 0) {
      placeholder = '— No options available —';
    }
    return (
      <div key={level.uid || depth} className="space-y-1.5">
        <label htmlFor={fieldId} className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
          <ListTree size={12} className="text-primary" />
          <span>{level.name}</span>
        </label>
        <select
          id={fieldId}
          className={selectClassName(false)}
          value={value[depth] || ''}
          disabled={options.length === 0}
          onChange={(e) => onChange(setHierarchyLevel(value, depth, e.target.value))}
        >
          <option value="">{placeholder}</option>
          {options.map((n) => (
            <option key={n.uid} value={n.uid}>
              {n.name}{n.isActive ? '' : ' (Inactive)'}
            </option>
          ))}
        </select>
      </div>
    );
  });

  if (inline) return <>{fields}</>;

  return (
    <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
      <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
        <div className="flex items-center gap-2">
          <ListTree size={18} className="text-primary" />
          <h2 className="text-base font-black text-base-content uppercase tracking-wider">
            Hierarchy Tree
          </h2>
        </div>
        <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields}
      </div>
    </div>
  );
};

HierarchySelector.propTypes = {
  levels: PropTypes.arrayOf(PropTypes.shape({
    uid: PropTypes.string,
    key: PropTypes.string,
    name: PropTypes.string.isRequired,
  })),
  nodes: PropTypes.arrayOf(PropTypes.shape({
    uid: PropTypes.string.isRequired,
    parentUid: PropTypes.string,
    depth: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
  })),
  value: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  // (hasError: boolean) => className
  selectClassName: PropTypes.func.isRequired,
  // Render only the level fields, without the card wrapper
  inline: PropTypes.bool,
};

export default HierarchySelector;
